import React from "react";
import {
	ClassNameProps,
	Column,
	If,
	makeClassName,
	Modal,
	NullablePick,
	Row,
	StyleProps,
	useTimeout,
	ViewableObserver,
} from "@hex-insights/core";
import { TextBlockLoadingPlaceholder, TextLineLoadingPlaceholder } from "../../../../Components/LoadingPlaceholder";
import {
	Person,
	PersonNameFragment,
	Post,
	useInitialIsViewed,
	User,
	useRecordView,
	UserNameAndImageFragment,
} from "../../../../Utilities";
import { MediaAttachmentUtilities } from "../../../Utilities";
import * as ModelComponents from "../../_General";
import * as MediaAttachmentComponents from "../../MediaAttachment";
import { SimpleSharingView, SimpleSharingViewProps } from "../../SharingRecord";
import * as SocialCommentComponents from "../../SocialComment";
import * as SocialReactionComponents from "../../SocialReaction";
import { PostAdminModal } from "../AdminModal";
import { AuthorTimestampView, AuthorTimestampViewProps } from "../General";
import { PostModal } from "../Modal";
import styles from "./styles.module.css";

export type TimelineItemPost = Pick<Post, "id" | "title" | "body" | "createdAt"> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
	mediaAttachments: (Pick<
		Post["mediaAttachments"][0],
		"id" | "title" | "description" | "isFile" | "fileURL" | "fileKey" | "fileContentType" | "linkURL"
	> & {
		taggedPeople: (Pick<Person, "id"> & PersonNameFragment & { user: NullablePick<Person["user"], "isCurrent"> })[];
	})[];
	socialReactions: SocialReactionComponents.ReactionsViewProps["reactions"];
	socialComments: SocialCommentComponents.CommentsViewProps["comments"];
	sharingRecord: SimpleSharingViewProps["sharingRecord"] | null;
};

export type TimelineItemProps = {
	isLoading?: boolean;
	post: TimelineItemPost | null;
	modalVariant?: "default" | "admin";
} & Partial<ClassNameProps & StyleProps>;

export function TimelineItem({
	isLoading = false,
	post,
	modalVariant = "default",
	className,
	style,
}: TimelineItemProps) {
	const taggedPeople = React.useMemo(
		() => MediaAttachmentUtilities.aggregateTaggedPeople(post?.mediaAttachments ?? []),
		[post],
	);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();
	const [modalAttachmentIndex, setModalAttachmentIndex] = React.useState<number | undefined>(undefined);
	const onClickAttachment = React.useCallback(
		(index: number) => {
			setModalAttachmentIndex(index);
			openModal();
		},
		[openModal],
	);
	const onClickOpenModal = React.useCallback(() => {
		setModalAttachmentIndex(undefined);
		openModal();
	}, [openModal]);

	const isTextOnly = post?.mediaAttachments.length === 0;

	const viewerReaction = post?.socialReactions.find((reaction) => reaction.author?.isCurrent) ?? null;
	const postID = post?.id;
	const reactionFormValues = React.useMemo(() => ({ postID: postID ?? null }), [postID]);

	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitAndExtractMediaAttachments(post?.mediaAttachments, post?.body),
		[post],
	);

	const [recordView] = useRecordView(post?.id ?? "0");
	const { setTimeout, clearTimeout } = useTimeout();
	const hasPost = !!post;
	// rate limit has to be managed externally because the enter and exit view calls should have separate limits (no
	// limit for exit) and because clearing the timeout to record the view should not count towards the limit.
	const { isLimitedRef, trigger: triggerRateLimit } = useRateLimit(100_000);
	const onEnterView = React.useCallback(() => {
		if (!hasPost || isLimitedRef.current) {
			return;
		}
		setTimeout(() => {
			recordView();
			triggerRateLimit();
		}, 1500);
	}, [hasPost, isLimitedRef, setTimeout, recordView, triggerRateLimit]);
	const onExitView = React.useCallback(() => {
		clearTimeout();
	}, [clearTimeout]);

	const ModalComponent = modalVariant === "admin" ? PostAdminModal : PostModal;

	if (!post && isLoading) {
		return (
			<div className={makeClassName(styles["timeline-item"], className)} style={style}>
				<Column justify="spaced-start">
					<ModelComponents.AuthorTimestampViewLoadingPlaceholder withTimestamp />
					<TextBlockLoadingPlaceholder style={{ maxWidth: "40rem" }} />
					<MediaAttachmentComponents.VisualAttachmentRowLoadingPlaceholder numAttachments={6} />
					<Row justify="space-between">
						<TextLineLoadingPlaceholder>Be the first to like this</TextLineLoadingPlaceholder>
						<TextLineLoadingPlaceholder style={{ marginLeft: "0.5rem" }}>View</TextLineLoadingPlaceholder>
					</Row>
				</Column>
			</div>
		);
	}

	if (!post) {
		return null;
	}

	const showTaggedPeople = taggedPeople.length > 0;
	const showSharing = !!post.sharingRecord && (post.sharingRecord.isPublic || post.sharingRecord.userGroups.length > 0);

	return (
		<div
			className={makeClassName(
				styles["timeline-item"],
				isTextOnly ? styles["timeline-item--text-only"] : "",
				className,
			)}
			style={style}
		>
			<Column justify="spaced-start">
				<Row justify="space-between">
					<TimelineItemAuthorTimestampView
						post={post}
						style={{ width: "100%", overflow: "hidden", marginRight: "1rem" }}
					/>
					<SocialReactionComponents.SocialReactionToggleButton
						instanceID={viewerReaction?.id}
						formValues={reactionFormValues}
						iconSize={24}
					/>
				</Row>
				<If condition={post.body !== ""}>
					<ModelComponents.TruncatedTextWithGeneralButton
						lines={3}
						onClick={onClickOpenModal}
						className={styles["timeline-item__body-text"]}
					>
						{post.body}
					</ModelComponents.TruncatedTextWithGeneralButton>
				</If>
				<If condition={showTaggedPeople || showSharing}>
					<Row
						justify="spaced-start"
						horizontalSpacing="1.5rem"
						verticalSpacing="0.5rem"
						withVerticalSpacing
						overflow="wrap"
					>
						{showTaggedPeople && (
							<MediaAttachmentComponents.TaggedPeopleView taggedPeople={taggedPeople} style={{ minWidth: 0 }} />
						)}
						{showSharing && post.sharingRecord && (
							<SimpleSharingView sharingRecord={post.sharingRecord} size="small" style={{ minWidth: 0 }} />
						)}
					</Row>
				</If>
				<If condition={visualAttachments.length > 0}>
					<MediaAttachmentComponents.VisualAttachmentsRow
						attachments={visualAttachments}
						onClickAttachment={onClickAttachment}
					/>
				</If>
				<If condition={fileAttachments.length > 0}>
					<MediaAttachmentComponents.FileAttachmentsRow attachments={fileAttachments} />
				</If>
				<If condition={linkAttachments.length > 0}>
					<MediaAttachmentComponents.LinkAttachmentsRow attachments={linkAttachments} />
				</If>

				<Row justify="space-between">
					<SocialReactionComponents.ReactionsView postID={post.id} reactions={post.socialReactions} />

					<SocialCommentComponents.CommentsViewButton
						numComments={post.socialComments.length}
						onClick={onClickOpenModal}
					/>
				</Row>
			</Column>

			<ViewableObserver onEnterView={onEnterView} onExitView={onExitView} />

			<Modal.If condition={isModalOpen}>
				<ModalComponent post={post} initialIndex={modalAttachmentIndex} onClose={closeModal} />
			</Modal.If>
		</div>
	);
}

type TimelineItemAuthorTimestampViewProps = Omit<AuthorTimestampViewProps, "post" | "isNew"> & {
	post: Pick<Post, "id"> & AuthorTimestampViewProps["post"];
};

function TimelineItemAuthorTimestampView({ post, ...props }: TimelineItemAuthorTimestampViewProps) {
	const isViewed = useInitialIsViewed(post.id);

	return <AuthorTimestampView post={post} isNew={!isViewed} {...props} />;
}

// TODO move to core (and rename)

function useRateLimit(limitMS: number) {
	const isLimitedRef = React.useRef(false);

	const { setTimeout } = useTimeout();
	const trigger = React.useCallback(() => {
		isLimitedRef.current = true;
		setTimeout(() => (isLimitedRef.current = false), limitMS);
	}, [setTimeout, limitMS]);

	return { isLimitedRef, trigger };
}
